/**
 * Wraps a dashboard or report page in its theme: the CSS variables from
 * report-theme.ts, plus the theme's web fonts, loaded once each on demand.
 */

import { useEffect, type ReactNode } from 'react';

import { isThemed, themeVariables, WEB_FONTS, type ReportTheme } from './report-theme.ts';

const FONT_HOST = 'https://fonts.googleapis.com/css2';

function loadFont(font: string | undefined): void {
  const family = font ? WEB_FONTS[font as keyof typeof WEB_FONTS] : undefined;
  if (!family) return;
  const id = `rt-font-${font}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `${FONT_HOST}?family=${family}&display=swap`;
  document.head.appendChild(link);
}

export function useThemeFonts(theme: ReportTheme): void {
  useEffect(() => {
    loadFont(theme.title_font);
    loadFont(theme.body_font);
  }, [theme.title_font, theme.body_font]);
}

export function ThemeScope({
  theme,
  layout,
  children,
}: {
  theme: ReportTheme | Record<string, unknown> | undefined;
  /** A dashboard paints the ground behind its grid; a report paints each page sheet. */
  layout: 'dashboard' | 'report';
  children: ReactNode;
}): ReactNode {
  const value = (theme ?? {}) as ReportTheme;
  useThemeFonts(value);
  if (!isThemed(value)) return children;
  return (
    <div className={`rt rt-${layout}`} style={themeVariables(value)}>
      {children}
    </div>
  );
}
